import { Entity } from "../filters/filter.types";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { portalApi } from "portal/state/portalApi";
import { SerializedError } from "@reduxjs/toolkit";
import { useMemo } from "react";
import { useQueryPopups } from "portal/utils/hooks/useApiPopups";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import { valueOrLoading } from "portal/utils/strings";

/** Robot options for prediction-point filters, optionally scoped to a customer UUID. */
export const useGetRobotFilterOptions = (
  scopeCustomerUuid?: string
): {
  robots?: Entity[];
  isLoading: boolean;
  error: FetchBaseQueryError | SerializedError | undefined;
} => {
  const { t } = useTranslation();
  const { isInternal } = useSelf();
  const filterByCustomerUuid = useMemo(
    () => scopeCustomerUuid ?? undefined,
    [scopeCustomerUuid]
  );

  const {
    data: robotSummaries,
    isLoading: isRobotsLoading,
    error: robotsError,
  } = useQueryPopups(
    portalApi.endpoints.listRobots.useQuery({ excludeMetrics: true }),
    {
      errorVariant: "warning",
    }
  );

  const robots = useMemo<Entity[] | undefined>(() => {
    if (!robotSummaries) {
      return;
    }
    const allRobots = [];
    for (const robotSummary of robotSummaries) {
      const { robot } = robotSummary;
      if (!robot) {
        continue;
      }
      const { customerSerial, serial } = robot;

      if (
        filterByCustomerUuid &&
        robotSummary.customer?.uuid !== filterByCustomerUuid
      ) {
        continue;
      }

      allRobots.push({
        id: serial,
        name: isInternal ? serial : valueOrLoading(customerSerial, t),
      });
    }
    return allRobots;
  }, [robotSummaries, filterByCustomerUuid, isInternal, t]);

  return {
    robots,
    isLoading: isRobotsLoading,
    error: robotsError,
  };
};
